import React, { useState } from 'react';
import { formatCurrency, toQuarter } from '../../helpers/format';
import useBalanceSheet from '../../hooks/useBalanceSheet';
import Table from '../Table/Table';
import style from './StockDetails.module.css';

const BalanceSheetTable = ({ stock }) => {
  const { balanceSheet } = useBalanceSheet(stock.id);
  const [showDetails, setShowDetails] = useState(false);

  const metricLinks = (() => {
    const symbol = stock?.symbol?.toLowerCase();
    if (!symbol) return {};
    return {
      current_cash: `/stocks/${symbol}/current-cash`,
      short_term_investments: `/stocks/${symbol}/short-term-investments`,
      receivables: `/stocks/${symbol}/receivables`,
      inventory: `/stocks/${symbol}/inventory`,
      other_current_assets: `/stocks/${symbol}/other-current-assets`,
      current_assets: `/stocks/${symbol}/current-assets`,
      long_term_investments: `/stocks/${symbol}/long-term-investments`,
      property_plant_equipment: `/stocks/${symbol}/property-plant-equipment`,
      goodwill: `/stocks/${symbol}/goodwill`,
      intangible_assets: `/stocks/${symbol}/intangible-assets`,
      other_assets: `/stocks/${symbol}/other-assets`,
      total_assets: `/stocks/${symbol}/total-assets`,
      net_tangible_assets: `/stocks/${symbol}/net-tangible-assets`,
      accounts_payable: `/stocks/${symbol}/accounts-payable`,
      other_current_liabilities: `/stocks/${symbol}/other-current-liabilities`,
      total_current_liabilities: `/stocks/${symbol}/total-current-liabilities`,
      long_term_debt: `/stocks/${symbol}/long-term-debt`,
      other_liabilities: `/stocks/${symbol}/other-liabilities`,
      minority_interest: `/stocks/${symbol}/minority-interest`,
      total_liabilities: `/stocks/${symbol}/total-liabilities`,
      common_stock: `/stocks/${symbol}/common-stock`,
    };
  })();

  if (!balanceSheet || !balanceSheet?.current) return null;

  if (showDetails) {
    return (
      <div>
        <DetailsTable balanceSheet={balanceSheet} group="assets" metricLinks={metricLinks} />
        <DetailsTable balanceSheet={balanceSheet} group="liabilities" metricLinks={metricLinks} />
        <DetailsTable balanceSheet={balanceSheet} group="equity" metricLinks={metricLinks} />
      </div>
    );
  } else {
    return <OverviewTable balanceSheet={balanceSheet} setShowDetails={setShowDetails} metricLinks={metricLinks} />;
  }
};

export default BalanceSheetTable;

function OverviewTable({ balanceSheet, setShowDetails, metricLinks }) {
  let change;

  return (
    <Table>
      <thead>
        <tr>
          <td colSpan="2">
            Balance Sheet
            &nbsp;-&nbsp;
            {balanceSheet?.current && toQuarter(balanceSheet.current.fiscal_date)}
          </td>
          <th width="5%">
            YOY
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th>
            {metricLinks.current_assets ? (
              <a
                href={metricLinks.current_assets}
                className={style.inlineLink}
                style={{ display: 'inline-flex', width: 'auto', padding: 0, margin: 0 }}
              >
                <span>Current Assets</span>
                <img
                  src="/react/phosphor/caret-right-bold.svg"
                  alt="Current Assets"
                  width="12"
                  height="12"
                  className={style.inlineLinkIcon}
                />
              </a>
            ) : (
              'Current Assets'
            )}
          </th>
          <td>
            {balanceSheet.current.current_assets < 0 ? '-' : ''}${formatCurrency(Math.abs(balanceSheet.current.current_assets), { compact: true })}
          </td>
          {(change = changeInPercent(balanceSheet?.current?.current_assets, balanceSheet?.previous?.current_assets)) && null}
          <td className={change ? change.startsWith('-') ? style.negativeChange : style.positiveChange : style.noChangeAvailable}>
            {change || '-'}
          </td>
        </tr>
        <tr>
          <th>
            {metricLinks.total_current_liabilities ? (
              <a
                href={metricLinks.total_current_liabilities}
                className={style.inlineLink}
                style={{ display: 'inline-flex', width: 'auto', padding: 0, margin: 0 }}
              >
                <span>Current Liabilities</span>
                <img
                  src="/react/phosphor/caret-right-bold.svg"
                  alt="Current Liabilities"
                  width="12"
                  height="12"
                  className={style.inlineLinkIcon}
                />
              </a>
            ) : (
              'Current Liabilities'
            )}
          </th>
          <td>
            {balanceSheet.current.total_current_liabilities < 0 ? '-' : ''}${formatCurrency(Math.abs(balanceSheet.current.total_current_liabilities), { compact: true })}
          </td>
          {(change = changeInPercent(balanceSheet?.current?.total_current_liabilities, balanceSheet?.previous?.total_current_liabilities)) && null}
          <td className={change ? change.startsWith('-') ? style.negativeChange : style.positiveChange : style.noChangeAvailable}>
            {change || '-'}
          </td>
        </tr>
        <tr>
          <th>Shareholder Equity</th>
          <td>
            {balanceSheet.current.shareholder_equity < 0 ? '-' : ''}${formatCurrency(Math.abs(balanceSheet.current.shareholder_equity), { compact: true })}
          </td>
          {(change = changeInPercent(balanceSheet?.current?.shareholder_equity, balanceSheet?.previous?.shareholder_equity)) && null}
          <td className={change ? change.startsWith('-') ? style.negativeChange : style.positiveChange : style.noChangeAvailable}>
            {change || '-'}
          </td>
        </tr>
        <tr>
          <th colSpan="3">
            <button onClick={() => setShowDetails(true)}>
              + Show Details
            </button>
          </th>
        </tr>
      </tbody>
    </Table>
  );
}

function DetailsTable({ balanceSheet, group, metricLinks }) {
  const rows = balanceSheetStructure[group].map((key) => {
    const change = changeInPercent(balanceSheet?.current?.[key], balanceSheet?.previous?.[key])
    return (
      <tr key={key}>
        <th>
          {metricLinks[key] ? (
            <a
              href={metricLinks[key]}
              className={style.inlineLink}
              style={{ display: 'inline-flex', width: 'auto', padding: 0, margin: 0 }}
            >
              <span>{formatKey(key)}</span>
              <img
                src="/react/phosphor/caret-right-bold.svg"
                alt={formatKey(key)}
                width="12"
                height="12"
                className={style.inlineLinkIcon}
              />
            </a>
          ) : (
            formatKey(key)
          )}
        </th>
        <td>
          {balanceSheet.current[key] < 0 ? '-' : ''}${formatCurrency(Math.abs(balanceSheet.current[key]), { compact: true })}
        </td>
        <td className={change ? change.startsWith('-') ? style.negativeChange : style.positiveChange : style.noChangeAvailable}>
          {change || '-'}
        </td>
      </tr>
    );
  });

  return (
    <Table>
      <thead>
        <tr>
          <td colSpan="2">
            {formatKey(group)}
            &nbsp;-&nbsp;
            {toQuarter(balanceSheet.current.fiscal_date)}
          </td>
          <th width="5%">YOY</th>
        </tr>
      </thead>

      <tbody>
        {rows}
      </tbody>
    </Table>
  );
}

function formatKey(key) {
  const words = key.split('_');
  return words.map((word) => word[0].toUpperCase() + word.slice(1)).join(' ');
}

function changeInPercent(currentValue, previousValue) {
  if (!currentValue || !previousValue) { return null; }
  const sign = currentValue > previousValue ? '+' : '';
  const change = ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
  return `${sign}${change.toFixed(1)}%`;
}

const balanceSheetStructure = {
  assets: ['current_cash', 'short_term_investments', 'receivables', 'inventory', 'other_current_assets', 'current_assets', 'long_term_investments', 'property_plant_equipment', 'goodwill', 'intangible_assets', 'other_assets', 'total_assets', 'net_tangible_assets'],
  liabilities: ['accounts_payable', 'other_current_liabilities', 'total_current_liabilities', 'long_term_debt', 'other_liabilities', 'minority_interest', 'total_liabilities'],
  equity: ['common_stock', 'retained_earnings', 'shareholder_equity'],
};